//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import React from 'react';
import { useSelector } from 'react-redux';

import { getIsRevealInsightsFeatureEnabled } from '@commerce/selectors/getIsRevealInsightsFeatureEnabled';
import { getIsGalleriesEnabled } from '@galleries/selectors/getIsGalleriesEnabled';
import {
  getIsLike2BuyEnabled,
  getIsLike2BuyTiktokEnabled,
} from '@insights/redux/selectors/getAvailableInsightsPages';
import { getIsLike2BuyV2Enabled } from '@instagram-account/redux/like2buy/selectors/getIsLike2BuyV2Enabled';
import { NavDropdownMenuItem } from '@legacy-components/nav3/navigation-menus/NavDropdownMenuItem';
import { NAV_MENU_ITEMS_TRACKING_ID } from '@legacy-components/nav3/navigation-menus/constants/NavMenuItemConstants';
import { StorefrontsMenu } from '@legacy-components/nav3/navigation-menus/dropdown-menus/StorefrontsMenu';
import { getIsGoogleAssetFeedsEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsAssetFeedTaggingEnabled';
import { getIsEmailBuilderEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsEmailBuilderEnabled';
import { getIsInstagramShopsUgcEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsInstagramShopsUgcEnabled';
import { getIsInstashopFeatureEnabled } from '@legacy-components/nav3/navigation-menus/selectors/getIsInstashopEnabled';
import { getIsTributarySelfServeEnabled } from '@media-library/redux/getMediaLibraryPage';
import { getIsPromotedPostsAccessEnabled } from '@publishing/analytics/selectors/getAnalyticsFeaturesEnabled';
import {
  getIsInstagramAdsContentAllowlistingEnabled,
  getIsTiktokSparkAdsEnabled,
} from '@redux/selectors/getIsPaidSocialFeatureEnabled';
import { getIsBazaarvoiceNetworkSyndicationFrontendEnabled } from '@uia/rights/selectors/getUIARights';

import { NavGroup } from 'app/initialization/routing/stateHelpers';

interface Props {
  setIsMenuOpen: (isMenuOpen: boolean) => void;
}

export const CommerceNav = ({ setIsMenuOpen }: Props) => {
  const isGalleriesEnabled = useSelector(getIsGalleriesEnabled);
  const isEmailBuilderEnabled = useSelector(getIsEmailBuilderEnabled);
  const isInstashopEnabled = useSelector(getIsInstashopFeatureEnabled);
  const isBazaarvoiceSyndicationEnabled = useSelector(
    getIsBazaarvoiceNetworkSyndicationFrontendEnabled,
  );
  const isRevealPageEnabled = useSelector(getIsRevealInsightsFeatureEnabled);
  const isInstagramAdsContentAllowlistingEnabled = useSelector(
    getIsInstagramAdsContentAllowlistingEnabled,
  );
  const isPromotedPostsEnabled = useSelector(getIsPromotedPostsAccessEnabled);
  const isTiktokSparkAdsEnabled = useSelector(getIsTiktokSparkAdsEnabled);
  const isLike2BuyEnabled = useSelector(getIsLike2BuyEnabled);
  const isLike2BuyV2Enabled = useSelector(getIsLike2BuyV2Enabled);
  const isLike2BuyTiktokEnabled = useSelector(getIsLike2BuyTiktokEnabled);
  const isTributarySelfServeEnabled = useSelector(
    getIsTributarySelfServeEnabled,
  );
  const isInstagramShopsUgcEnabled = useSelector(getIsInstagramShopsUgcEnabled);
  const isGoogleAssetFeedsEnabled = useSelector(getIsGoogleAssetFeedsEnabled);

  const isSocialVisible =
    isLike2BuyEnabled ||
    isLike2BuyV2Enabled ||
    isLike2BuyTiktokEnabled ||
    isTributarySelfServeEnabled ||
    isInstagramShopsUgcEnabled;

  const isPaidSocialVisible =
    isInstagramAdsContentAllowlistingEnabled ||
    isPromotedPostsEnabled ||
    isTiktokSparkAdsEnabled;

  const isVisualSyndicationVisible =
    isBazaarvoiceSyndicationEnabled && !isInstashopEnabled;

  const isCommerceEnabled =
    isSocialVisible ||
    isVisualSyndicationVisible ||
    isGalleriesEnabled ||
    isPaidSocialVisible ||
    isEmailBuilderEnabled ||
    isRevealPageEnabled ||
    isGoogleAssetFeedsEnabled;

  if (!isCommerceEnabled) {
    return null;
  }

  return (
    <NavDropdownMenuItem
      activeNavGroup={[NavGroup.COMMERCE]}
      content={<StorefrontsMenu />}
      setIsMenuOpen={setIsMenuOpen}
      trackingId={NAV_MENU_ITEMS_TRACKING_ID.COMMERCE_NAV}
    >
      Commerce
    </NavDropdownMenuItem>
  );
};
